import React, {useState} from 'react';
import {Alert, Button, Stack, TextField} from "@mui/material"
import ModalBase from "../modal/ModalBase"
import AsyncButton from "../button/AsyncButton"
import ApiError from "../../api/ApiError"
import {ProjectJson} from "../../api/json/ProjectJson"
import {AnyTemplateJson, TemplateChatParts} from "../../api/json/TemplateJson"
import {TemplateType} from "../../api/enum/TemplateType"
import {TemplateRequests} from "../../api/requests/TemplateRequests"
import TemplateEditorChat from "./TemplateEditorChat"
import {ProjectType} from "../../api/enum/ProjectType"
import ClassifierTemplateUtil from "../../library/ClassifierTemplateUtil"

interface NewTemplateModalProps {
    project: ProjectJson
    open: boolean
    setOpen: (open: boolean) => void
    onCreated: (template: AnyTemplateJson) => void
}

function NewTemplateModal({project, open, setOpen, onCreated}: NewTemplateModalProps) {
    const isClassifier = project.type === ProjectType.CLASSIFIER
    const blankParts = (): TemplateChatParts => ({
        type: TemplateType.CHAT,
        system: "",
        user: "",
        assistant: isClassifier ? ClassifierTemplateUtil.defaultOutput(project.columns) : "",
    })

    const [name, setName] = useState("")
    const [parts, setParts] = useState<TemplateChatParts>(blankParts)

    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<ApiError>()

    const classifierOutputError = isClassifier
        ? ClassifierTemplateUtil.validateOutput(parts.assistant, project.columns)
        : null
    const empty = parts.user.length === 0 && parts.assistant.length === 0 && parts.system.length === 0
    const disabled = empty || !!classifierOutputError

    const handleCreate = () => {
        setSaving(true)
        TemplateRequests.create(project.id, name.length > 0 ? name : null, parts)
            .then(created => {
                setError(undefined)
                onCreated(created)
                setOpen(false)
                // Reset for next open
                setName("")
                setParts(blankParts())
            })
            .catch(setError)
            .finally(() => setSaving(false))
    }

    return (
        <ModalBase
            open={open}
            setOpen={setOpen}
            size="large"
            title="New template"
            blockBackdropClick
            buttons={
                <>
                    <Button variant="outlined"
                            color="secondary"
                            onClick={() => setOpen(false)}>
                        Cancel
                    </Button>
                    <AsyncButton variant="contained"
                                 disabled={disabled}
                                 waiting={saving}
                                 onClick={handleCreate}
                    >
                        Create template
                    </AsyncButton>
                </>
            }
            error={error}
        >
            <Stack spacing={2}>
                <TextField label="Name (optional)"
                           value={name}
                           onChange={e => setName(e.target.value.slice(0, 60))}
                />
                <TemplateEditorChat columns={project.columns}
                                    templateParts={parts}
                                    classifierOutput={isClassifier}
                                    onChange={setParts}
                />
                {classifierOutputError &&
                    <Alert severity="error">{classifierOutputError}</Alert>}
            </Stack>
        </ModalBase>
    )
}

export default NewTemplateModal
